import {useCallback, useEffect, useMemo, useState} from 'react';
import {useGetConfigurationQuery} from 'store/customer/appSlice';

type useRecaptchaReturnType<SubmitPassThru_T extends object = object> = {
    handleRecaptchaSubmit: (passThruArg: SubmitPassThru_T) => void;

    /**
     * Indicates whether the ReCaptcha script is currently loading.
     *
     * Value will always be `false` if ReCaptcha is not enabled.
     */
    isRecaptchaLoading: boolean;

    /**
     * Indicates whether the ReCaptcha script has been loaded.
     *
     * Value will always be `false` if ReCaptcha is not enabled.
     */
    isRecaptchaLoaded: boolean;
};

/*
 * Injects the Google ReCaptcha v3 script into the page.
 *
 * Provides a callback to be used utilised in form submissions, then
 * calls the provided 'real' submit handler.
 */
export const useRecaptcha = <
    SubmitPassThru_T extends object,
    Submit_T extends (
        recaptchaToken: string,
        passThruArg: SubmitPassThru_T
    ) => void,
>(
    submitAction: string,
    submitHandler: Submit_T
): useRecaptchaReturnType<SubmitPassThru_T> => {
    const {data: configuration, isLoading: isConfigurationLoading} =
        useGetConfigurationQuery();

    const recaptchaSiteKey = useMemo(
        () => configuration?.googleRecaptchaKey,
        [isConfigurationLoading]
    );
    const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(false);
    const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);

    useEffect(() => {
        if (
            !recaptchaSiteKey || // undefined || null
            isRecaptchaLoading ||
            isRecaptchaLoaded
        ) {
            return;
        }

        setIsRecaptchaLoading(true);

        const script = document.createElement('script');

        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
        script.async = true;
        script.onload = () => {
            setIsRecaptchaLoading(false);
            setIsRecaptchaLoaded(true);
        };

        document.body.appendChild(script);
    }, [recaptchaSiteKey, isRecaptchaLoaded, isRecaptchaLoading]);

    const handleRecaptchaSubmit = useCallback(
        (passThruArg: SubmitPassThru_T) => {
            if (!isRecaptchaLoaded && !isRecaptchaLoading) {
                // Recaptcha is not enabled ...
                submitHandler(null, passThruArg);
                return;
            }

            // Continue with the submission ...
            grecaptcha.enterprise.ready(async () => {
                const recaptchaToken = await grecaptcha.enterprise.execute(
                    recaptchaSiteKey,
                    {action: submitAction}
                );

                submitHandler(recaptchaToken, passThruArg);
            });
        },
        [recaptchaSiteKey, isRecaptchaLoaded, isRecaptchaLoading]
    );

    return {
        handleRecaptchaSubmit,
        isRecaptchaLoading,
        isRecaptchaLoaded,
    };
};
